import { msg, batch, tick } from '@boba-cli/tea';

// src/messages.ts
var TickMsg = class {
  constructor(id, tag, timeout) {
    this.id = id;
    this.tag = tag;
    this.timeout = timeout;
  }
  _tag = "timer-tick";
};
var TimeoutMsg = class {
  constructor(id) {
    this.id = id;
  }
  _tag = "timer-timeout";
};
var StartStopMsg = class {
  constructor(id, running) {
    this.id = id;
    this.running = running;
  }
  _tag = "timer-start-stop";
};
var lastId = 0;
function nextId() {
  return ++lastId;
}
var TimerModel = class _TimerModel {
  timeout;
  interval;
  #id;
  #tag;
  #running;
  constructor(options) {
    this.timeout = options.timeout;
    this.interval = options.interval;
    this.#running = options.running;
    this.#id = options.id;
    this.#tag = options.tag;
  }
  /** Create a new timer with the given options. */
  static new(options) {
    const interval = options.interval ?? 1e3;
    return new _TimerModel({
      timeout: options.timeout,
      interval,
      running: true,
      id: nextId(),
      tag: 0
    });
  }
  /** Create a new timer with explicit timeout and interval. */
  static withInterval(timeout, interval) {
    return _TimerModel.new({ timeout, interval });
  }
  /** Unique ID for message routing. */
  id() {
    return this.#id;
  }
  /** Whether the timer is currently running (false once timed out). */
  running() {
    if (this.timedOut()) {
      return false;
    }
    return this.#running;
  }
  /** Whether the timer has expired. */
  timedOut() {
    return this.timeout <= 0;
  }
  /** Start ticking. */
  init() {
    return this.tick();
  }
  /** Update the timer in response to a message. */
  update(msg$1) {
    if (msg$1 instanceof StartStopMsg) {
      if (msg$1.id !== 0 && msg$1.id !== this.#id) {
        return [this, null];
      }
      const next = new _TimerModel({
        timeout: this.timeout,
        interval: this.interval,
        running: msg$1.running,
        id: this.#id,
        tag: this.#tag
      });
      return [next, next.tick()];
    }
    if (msg$1 instanceof TickMsg) {
      if (!this.running() || msg$1.id !== 0 && msg$1.id !== this.#id) {
        return [this, null];
      }
      if (msg$1.tag > 0 && msg$1.tag !== this.#tag) {
        return [this, null];
      }
      const nextTimeout = this.timeout - this.interval;
      const nextTag = this.#tag + 1;
      const next = new _TimerModel({
        timeout: nextTimeout,
        interval: this.interval,
        running: this.#running,
        id: this.#id,
        tag: nextTag
      });
      const timeoutCmd = next.timedOut() ? msg(new TimeoutMsg(this.#id)) : null;
      const tickCmd = next.timedOut() ? null : next.tick();
      return [next, batch(tickCmd, timeoutCmd)];
    }
    return [this, null];
  }
  /** Render remaining time as a human-readable string. */
  view() {
    return formatDuration(Math.max(0, this.timeout));
  }
  /** Command to start the timer. */
  start() {
    return this.startStop(true);
  }
  /** Command to stop/pause the timer. */
  stop() {
    return this.startStop(false);
  }
  /** Command to toggle running state. */
  toggle() {
    return this.startStop(!this.running());
  }
  tick() {
    const id = this.#id;
    const tag = this.#tag;
    return tick(this.interval, () => new TickMsg(id, tag, this.timedOut()));
  }
  startStop(running) {
    return msg(new StartStopMsg(this.#id, running));
  }
};
function formatDuration(ms) {
  const seconds = Math.floor(ms / 1e3) % 60;
  const minutes = Math.floor(ms / 6e4) % 60;
  const hours = Math.floor(ms / 36e5);
  if (hours > 0) return `${hours}h${minutes}m${seconds}s`;
  if (minutes > 0) return `${minutes}m${seconds}s`;
  return `${seconds}s`;
}

export { StartStopMsg, TickMsg, TimeoutMsg, TimerModel };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map